import React, { useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { PageCallback } from "react-pdf/dist/cjs/shared/types";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Modal, Stack } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { opportunityStatuses } from "constants/deal";
import { useNavigate } from "hooks";
import { useDealActions } from "store";
import { IOpportunities, IPortfolioConstructionOpportunity } from "store/types";

interface Props {
  opportunityData: IOpportunities | IPortfolioConstructionOpportunity[] | null;
  onClose: () => void;
  isOpen: boolean;
}

export const OpportunityPdfPreview: React.FC<Props> = ({ opportunityData, onClose, isOpen }) => {
  const { updateOpportunitiesStatusAction, getOpportunitiesAction } = useDealActions();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageScale, setPageScale] = useState<number>(0);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const contentRef = useRef<HTMLInputElement | null>(null);

  const opportunities = Array.isArray(opportunityData) ? opportunityData : [opportunityData];
  const currentOpportunity = useMemo(() => opportunities[currentIndex], [currentIndex, opportunities]);
  const isMultipleOpportunities = opportunities.length > 1;

  const handleClose = () => {
    setCurrentIndex(0);
    onClose();
  };

  const onOpportunityStatusChange = async (status: string) => {
    if (!currentOpportunity?.id) return;
    if (status === opportunityStatuses.INTERESTED) {
      await updateOpportunitiesStatusAction({ id: currentOpportunity.id, status: status });
      currentOpportunity.deal_id && navigate({ route: "deal", params: { deal_id: currentOpportunity.deal_id } });
    }
    if (status === opportunityStatuses.LATER) {
      await updateOpportunitiesStatusAction({ id: currentOpportunity.id, status: status });
    }
    await getOpportunitiesAction();
    handleClose();
  };

  const handleNext = () => {
    if (currentIndex < opportunities.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const scaleCalc = (page: PageCallback) => {
    if (contentRef.current) {
      setPageScale((contentRef.current.clientWidth - 40) / page.originalWidth);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const HTMLcontent = (htmlString: string) => {
    return <iframe style={{ width: "100%", height: "100%", border: "none" }} srcDoc={htmlString}></iframe>;
  };

  const PDFContent = (src: string) => {
    const renderPages = () => {
      const pages = [];
      for (let pageNumber = 1; pageNumber <= (numPages || 0); pageNumber++) {
        pages.push(
          <Page
            scale={pageScale}
            onRenderSuccess={(page) => {
              scaleCalc(page);
              //needed to avoid the problem with "black flash"
              if (pageNumber === numPages) {
                setLoading(false);
              }
            }}
            loading=""
            key={pageNumber}
            pageNumber={pageNumber}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        );
      }
      return pages;
    };

    return (
      <>
        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", width: "100%" }}>
            <CircularProgress size={200} />
          </Stack>
        )}
        <Stack ref={contentRef}>
          <Document renderMode="canvas" loading="" file={src} onLoadSuccess={onDocumentLoadSuccess}>
            {renderPages()}
          </Document>
        </Stack>
      </>
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}
    >
      <Stack
        sx={{
          position: "relative",
          flexGrow: 1,
          maxWidth: "60%",
          height: "80%",
          borderRadius: "12px",
          overflow: "hidden",
          backgroundColor: "#fff"
        }}
      >
        <Stack alignSelf="flex-end">
          <IconButton onClick={handleClose}>
            <CloseIcon sx={{ fontSize: "24px", color: "#0A3142" }} />
          </IconButton>
        </Stack>
        {isMultipleOpportunities && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              position: "absolute",
              top: "50%",
              width: "100%",
              px: 2,
              transform: "translateY(-50%)",
              zIndex: 1
            }}
          >
            <IconButton
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              sx={{
                backgroundColor: "white",
                "&:hover": { backgroundColor: "white" },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(255, 255, 255, 0.7)"
                }
              }}
            >
              <ArrowBackIosIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              disabled={currentIndex === opportunities.length - 1}
              sx={{
                backgroundColor: "white",
                "&:hover": { backgroundColor: "white" },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(255, 255, 255, 0.7)"
                }
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Stack>
        )}
        <Stack ref={contentRef} sx={{ width: "100%", height: "100%", overflow: "scroll", padding: "0 20px 20px 20px" }}>
          {currentOpportunity?.pdf_url
            ? PDFContent(currentOpportunity.pdf_url || "")
            : HTMLcontent(currentOpportunity?.html || "")}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-around"
          gap="10px"
          sx={{
            borderRadius: "48px",
            backgroundColor: "#fff",
            padding: "15px",
            position: "absolute",
            boxShadow: "0 4px 10px 0 rgba(0, 0, 0, 0.2)",
            left: "50%",
            bottom: "10%",
            transform: "translateX(-50%)"
          }}
        >
          <Button onClick={() => onOpportunityStatusChange(opportunityStatuses.LATER)} variant="outlined">
            Not Interested
          </Button>
          <Button
            onClick={() => {
              onOpportunityStatusChange(opportunityStatuses.INTERESTED);
            }}
            variant="contained"
          >
            Add to My Pipeline
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
